import { jsx, jsxs } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { inNextServer, getDefaultRadixPortalContainer, getRadixModalModeEnabled } from '@fabric-app/compat';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { styled } from 'styled-components';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { UpgradePromptModalContent } from './UpgradePromptModalContent.js';
import { SubscriptionTier } from '@fabric/woody-client';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { getUpsellTitle } from './utils/getUpsellTitle.js';
import { getUpsellLimitBadgeText } from './utils/getUpsellLimitBadgeText.js';
import { getPlanByTier, getPlanWithUpgradedEntitlement } from '../../pricing.utils.js';
import { useQuerySubscription } from '../../queries/useQuerySubscription.js';
import { toast } from '../../../store/alerts.js';
import { getToastMessageCapHit } from './utils/getToastMessageCapHit.js';
import { UpgradePromptModalContent_CreditsTopUp } from './UpgradePromptModalContent_CreditsTopUp.js';

const Backdrop = styled.div.withConfig({
  displayName: "UpgradePromptModal__Backdrop",
  componentId: "sc-52d29e5f-0"
})([
  `z-index:125;position:fixed;top:0;left:0;width:100%;height:100%;background-color:rgba(128,128,128,0.3);`,
  `{display:none;&[data-no-bottom-gap='true']{bottom:0;}}`
], mediaMobile);
const UpgradePromptModal = (props) => {
  const { title: titleProp, limitBadge: limitBadgeProp, noBottomGap = false, onClose, tier: tierProp, planEntitlement } = props;
  const { data: subscription } = useQuerySubscription();
  const currentTier = subscription?.tier || SubscriptionTier.FREE;
  const { upsellPlan, title, limitBadge, currentPlan } = useMemo(() => {
    const currentPlan2 = getPlanByTier(currentTier);
    const upsellPlan2 = tierProp ? getPlanByTier(tierProp) : getPlanWithUpgradedEntitlement(currentPlan2, planEntitlement);
    if (!upsellPlan2) {
      return {
        currentPlan: currentPlan2,
        upsellPlan: null,
        title: null,
        limitBadge: null
      };
    }
    const value = {
      currentPlan: currentPlan2,
      upsellPlan: upsellPlan2,
      title: titleProp || getUpsellTitle(currentPlan2, upsellPlan2, planEntitlement),
      limitBadge: limitBadgeProp || getUpsellLimitBadgeText(currentPlan2, planEntitlement)
    };
    return value;
  }, [
    tierProp,
    currentTier,
    planEntitlement,
    limitBadgeProp,
    titleProp
  ]);
  useRunOnce(() => {
    if (planEntitlement === "creditCentsMonthly") {
      return;
    }
    toast({
      content: getToastMessageCapHit(currentPlan, planEntitlement)
    });
    if (planEntitlement !== "maxFileSizeMB") {
      captureException(new Error("Upgrade prompt modal opened with no tier to upgrade"), {
        extra: {
          currentPlan,
          subscription,
          planEntitlement
        }
      });
    }
    onClose();
  }, {
    condition: !upsellPlan
  });
  if (inNextServer()) return null;
  return /* @__PURE__ */ jsx(Modal.Root, {
    open: true,
    onOpenChange: onClose,
    children: /* @__PURE__ */ jsxs(Modal.Portal, {
      container: getDefaultRadixPortalContainer(),
      children: [
        getRadixModalModeEnabled() ? /* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
          asChild: true,
          children: /* @__PURE__ */ jsx(Backdrop, {
            "data-no-bottom-gap": noBottomGap,
            onClick: onClose
          })
        }) : /* @__PURE__ */ jsx(Backdrop, {
          "data-no-bottom-gap": noBottomGap,
          onClick: onClose
        }),
        upsellPlan ? (
          /**
          * we have a plan to upsell, show the upsell content
          */
          /* @__PURE__ */ jsx(UpgradePromptModalContent, {
            ...props,
            upsellPlan,
            currentPlan,
            tier: upsellPlan.tier,
            title,
            limitBadge
          })
        ) : planEntitlement === "creditCentsMonthly" ? (
          /**
          * we don't have an upsell plan, if credits are depleted, show the top up content
          */
          /* @__PURE__ */ jsx(UpgradePromptModalContent_CreditsTopUp, {
            onClose
          })
        ) : null
      ]
    })
  });
};

export { UpgradePromptModal as default };
//# sourceMappingURL=UpgradePromptModal.js.map
